import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { STATE_STYLE, STATE_ORDER } from "@/components/StateBadge";
import { Truck, Search, MapPin, User, Package, Camera, Clock, ArrowLeft } from "lucide-react";
import { useLiveEvents } from "@/lib/useLiveEvents";

function StepDot({ done, active }) {
  return (
    <div className="relative flex items-center justify-center">
      <div
        className={`w-4 h-4 rounded-full border-2 ${
          active
            ? "border-yellow-300 bg-yellow-300"
            : done
            ? "border-yellow-300 bg-yellow-300"
            : "border-zinc-700 bg-zinc-950"
        }`}
      />
      {active && (
        <div className="absolute inset-[-8px] rounded-full border border-yellow-300 animate-ping" />
      )}
    </div>
  );
}

export default function PublicTracking() {
  const { tn } = useParams();
  const nav = useNavigate();
  const [input, setInput] = useState(tn || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (val) => {
    if (!val) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/tracking/${val.toUpperCase()}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tn) load(tn);
  }, [tn]);

  // Live updates when tracking is loaded
  useLiveEvents({
    tracking: tn,
    enabled: !!tn,
    onEvent: (evt) => {
      if (evt.type === "order_update" || evt.type === "order_state_changed" || evt.type === "order_assigned" || evt.type === "order_scanned") {
        if (tn) load(tn);
      }
    },
  });

  const search = (e) => {
    e.preventDefault();
    if (input.trim()) nav(`/rastreo/${input.trim().toUpperCase()}`);
  };

  const currentIdx = data ? STATE_ORDER.indexOf(data.state) : -1;

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise">
      <header className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src="/img/voycargo-logo.png" alt="VoyCargo Express" className="w-10 h-10 object-contain" />
            <span className="font-mono-tech text-[10px] px-1.5 py-0.5 bg-white/10 border border-white/20 text-yellow-300 tracking-widest">EXPRESS</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-yellow-300">
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">/ Rastreo público</div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-8">
          Rastrea tu pedido.
        </h1>

        <form onSubmit={search} className="flex items-stretch max-w-2xl border border-white/20 mb-12">
          <div className="flex items-center px-4 border-r border-white/10">
            <Search className="w-5 h-5 text-yellow-300" />
          </div>
          <input
            data-testid="tracking-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ej: VOY-20260201-A3B7C9"
            className="flex-1 bg-transparent px-4 py-4 outline-none font-mono-tech text-sm"
          />
          <button data-testid="tracking-search" className="bg-yellow-300 text-black font-bold px-6 uppercase tracking-widest text-sm hover:bg-yellow-200">
            Buscar
          </button>
        </form>

        {loading && (
          <div className="border border-white/10 p-8 font-mono-tech text-zinc-500 text-sm">Cargando...</div>
        )}

        {error && !loading && (
          <div className="border border-red-500/30 bg-red-500/5 p-8" data-testid="tracking-error">
            <div className="font-mono-tech text-xs text-red-300 uppercase tracking-widest mb-2">/ Error</div>
            <div className="font-display font-bold text-xl">{error}</div>
            <div className="text-zinc-500 text-sm mt-2">Verifica el número de rastreo e intenta nuevamente.</div>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-8" data-testid="tracking-result">
            <div className="flex items-center gap-2 mb-2">
              <div className="inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 px-3 py-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="font-mono-tech text-[10px] text-emerald-300 uppercase tracking-widest">Live · actualiza en tiempo real</span>
              </div>
            </div>
            {/* Summary */}
            <div className="border border-white/10 bg-zinc-900/60">
              <div className="p-6 md:p-8 border-b border-white/10 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest mb-2">Número de rastreo</div>
                  <div className="font-mono-tech text-2xl md:text-3xl font-bold">{data.tracking_number}</div>
                </div>
                <div className={`inline-flex items-center gap-2 px-4 py-2 border ${STATE_STYLE[data.state]?.color}`}>
                  <span className="w-2 h-2 rounded-full bg-current" />
                  <span className="font-mono-tech uppercase tracking-widest text-xs">{STATE_STYLE[data.state]?.label}</span>
                </div>
              </div>
              <div className="grid md:grid-cols-3 divide-x divide-white/10">
                <div className="p-6">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono-tech uppercase tracking-widest mb-2"><User className="w-3.5 h-3.5" /> Destinatario</div>
                  <div className="font-medium">{data.destinatario_nombre}</div>
                  <div className="text-zinc-400 text-sm mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.destinatario_comuna}</div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono-tech uppercase tracking-widest mb-2"><Package className="w-3.5 h-3.5" /> Remitente</div>
                  <div className="font-medium">{data.remitente}</div>
                  {data.descripcion && <div className="text-zinc-400 text-sm mt-1">{data.descripcion}</div>}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono-tech uppercase tracking-widest mb-2"><Clock className="w-3.5 h-3.5" /> Última actualización</div>
                  <div className="font-mono-tech text-sm">{new Date(data.updated_at).toLocaleString("es-CL")}</div>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="border border-white/10 bg-zinc-900/60 p-6 md:p-8">
              <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest mb-6">/ Progreso</div>
              <div className="grid grid-cols-6 gap-2">
                {STATE_ORDER.map((s, i) => (
                  <div key={s} className="flex flex-col items-center gap-3">
                    <StepDot done={i < currentIdx} active={i === currentIdx} />
                    <div className={`text-[10px] font-mono-tech uppercase tracking-widest text-center ${i <= currentIdx ? "text-yellow-300" : "text-zinc-600"}`}>
                      {STATE_STYLE[s]?.label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 h-px bg-white/10 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-yellow-300 transition-all duration-500"
                  style={{ width: `${Math.max(0, (currentIdx / (STATE_ORDER.length - 1)) * 100)}%` }}
                />
              </div>
            </div>

            {/* Delivery photo — solo se muestra en panel autenticado, NO en tracking público.
                En tracking público se indica que existe evidencia pero no se expone. */}
            {data.has_delivery_photo && (
              <div className="border border-white/10 bg-zinc-900/60 p-6" data-testid="delivery-photo-hint">
                <div className="flex items-center gap-2 font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-4"><Camera className="w-4 h-4" /> Evidencia de entrega</div>
                <div className="text-sm text-zinc-400">La evidencia fotográfica está disponible para el remitente en su panel privado.</div>
              </div>
            )}

            {/* History */}
            <div className="border border-white/10 bg-zinc-900/60">
              <div className="p-6 border-b border-white/10 font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">
                / Historial
              </div>
              <div className="divide-y divide-white/5">
                {[...data.history].reverse().map((h, i) => (
                  <div key={i} className="p-6 flex items-start gap-4">
                    <div className={`w-2 h-2 mt-2 rounded-full ${i === 0 ? "bg-yellow-300 pulse-dot" : "bg-zinc-600"}`} />
                    <div className="flex-1">
                      <div className="font-display font-bold">{h.state_label || h.state}</div>
                      {h.reason && <div className="text-sm text-red-300 mt-1">Motivo: {h.reason}</div>}
                      {h.comentario && <div className="text-sm text-zinc-400 mt-1">{h.comentario}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono-tech text-sm">{new Date(h.timestamp).toLocaleString("es-CL")}</div>
                      <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{h.user_role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
